import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LiveTrackingMap from './LiveTrackingMap';

interface LiveTrackingModalProps {
    isOpen: boolean;
    onClose: () => void;
    orderId: string;
    clientLocation?: { lat: number, lng: number };
    restaurantLocation?: { lat: number, lng: number };
    livreurId?: string;
    restaurantName?: string;
}

const LiveTrackingModal: React.FC<LiveTrackingModalProps> = ({
    isOpen,
    onClose,
    orderId,
    clientLocation,
    restaurantLocation,
    livreurId,
    restaurantName
}) => {
    const [estimate, setEstimate] = React.useState<number | null>(null);

    // Support for Android Hardware Back Button
    useEffect(() => {
        const handleHardwareBack = (e: Event) => {
            if (isOpen) {
                console.log("🛡️ [LiveTrackingModal] Intercepted Back Button - Closing");
                onClose();
                e.preventDefault();
            }
        };

        window.addEventListener('yumii:hardwareBack', handleHardwareBack);
        return () => window.removeEventListener('yumii:hardwareBack', handleHardwareBack);
    }, [isOpen, onClose]);

    // Lock scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const modalContent = (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[999] flex flex-col items-center justify-end sm:justify-center p-0 sm:p-6 bg-black/60 backdrop-blur-sm shadow-2xl">
                    {/* Backdrop click to close */}
                    <div className="absolute inset-0 z-0" onClick={onClose} />

                    <motion.div
                        initial={{ opacity: 0, y: 100, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 100, scale: 0.95 }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="relative z-10 w-full max-w-4xl h-[92vh] sm:h-[85vh] bg-white sm:rounded-[2.5rem] rounded-t-[2.5rem] overflow-hidden flex flex-col shadow-2xl"
                    >
                        {/* Header */}
                        <div className="px-6 py-5 bg-white border-b border-gray-100 flex justify-between items-center shrink-0">
                            <div>
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h2 className="text-xl font-black text-gray-900 leading-tight tracking-tight uppercase">{restaurantName || 'Suivi en direct'}</h2>
                                    <div className="flex h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                                </div>
                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em]">#{orderId.replace('ord_', '')} • En direct</p>
                            </div>

                            <button
                                onClick={onClose}
                                className="w-10 h-10 bg-gray-50 hover:bg-gray-100 text-gray-500 rounded-2xl flex items-center justify-center transition-colors shadow-sm active:scale-90"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Map Area */}
                        <div className="flex-1 w-full bg-gray-50 relative overflow-hidden">
                            <LiveTrackingMap
                                orderId={orderId}
                                clientLocation={clientLocation}
                                restaurantLocation={restaurantLocation}
                                livreurId={livreurId}
                                fullHeight
                                onEstimateChange={setEstimate}
                            />
                        </div>

                        {/* Footer Info Overlay (Optional extra info) */}
                        <div className="absolute bottom-6 left-6 right-6 z-[500] pointer-events-none">
                            <div className="bg-white/95 backdrop-blur-md p-5 rounded-[2rem] shadow-2xl border border-white/20 flex items-center justify-between pointer-events-auto">
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 ${estimate !== null ? 'bg-green-600 shadow-green-200' : 'bg-red-600 shadow-red-200'} rounded-2xl flex flex-col items-center justify-center text-white shadow-lg transition-colors duration-500`}>
                                        {estimate !== null ? (
                                            <>
                                                <span className="text-[10px] font-black leading-none mb-0.5">MIN</span>
                                                <span className="text-lg font-black leading-none">{estimate}</span>
                                            </>
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                                            </svg>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">Estimation d'arrivée</p>
                                        <p className="text-sm font-black text-gray-900">
                                            {estimate === 0
                                                ? "Le livreur est arrivé !"
                                                : estimate !== null
                                                    ? "Votre commande arrive !"
                                                    : "Localisation du livreur..."}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="ml-8 px-6 py-2.5 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-colors shadow-lg shrink-0"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );

    return createPortal(modalContent, document.body);
};

export default LiveTrackingModal;
